/**
 * Category Select Component.
 *
 * A domain-specific wrapper around ComboSelect for asset categories.
 * Flattens the hierarchical category tree, shows breadcrumb paths,
 * and supports permission-gated inline creation of new categories.
 *
 * New categories are added optimistically to the local options list
 * immediately, then persisted to the backend in the background.
 */

import { useState, useMemo, useCallback, useEffect } from "react";
import { apiClient } from "@/api/client";
import { useOrgConfig } from "@/features/settings/api";
import { usePermissions } from "@/hooks/usePermissions";
import type { CategoryOption } from "@/types/settings";
import { toast } from "@/stores/toastStore";
import ComboSelect from "@/components/ui/ComboSelect";
import type { ComboOption } from "@/components/ui/ComboSelect";

interface FlatCategory {
  value: string;
  label: string;
  path: string;
  depth: number;
}

interface CategorySelectProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

function flattenCategories(
  cats: CategoryOption[],
  parentPath: string[] = [],
): FlatCategory[] {
  const result: FlatCategory[] = [];
  for (const cat of cats) {
    const currentPath = [...parentPath, cat.label];
    result.push({
      value: cat.value,
      label: cat.label,
      path: parentPath.length > 0 ? parentPath.join(" > ") : "",
      depth: parentPath.length,
    });
    if (cat.children && cat.children.length > 0) {
      result.push(...flattenCategories(cat.children, currentPath));
    }
  }
  return result;
}

function CategorySelect({
  value,
  onChange,
  label,
  required = false,
  disabled = false,
  className,
}: CategorySelectProps) {
  const { data: config } = useOrgConfig();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission("settings.manage");

  // Local category list — initialized from server, extended optimistically.
  const [localCategories, setLocalCategories] = useState<CategoryOption[]>([]);

  // Sync from server config when it loads or changes.
  useEffect(() => {
    const prodCats = config?.products?.asset_categories;
    const invCats = config?.inventory?.asset_categories;
    const serverCats =
      prodCats && prodCats.length > 0
        ? prodCats
        : invCats && invCats.length > 0
          ? invCats
          : [];
    setLocalCategories((prev) => {
      // Merge: keep any locally-added categories not yet on the server.
      const serverValues = new Set(serverCats.map((c) => c.value));
      const localOnly = prev.filter((c) => !serverValues.has(c.value));
      return [...serverCats, ...localOnly];
    });
  }, [config]);

  const flatCats = useMemo(
    () => flattenCategories(localCategories),
    [localCategories],
  );

  const options: ComboOption[] = useMemo(
    () =>
      flatCats.map((c) => ({
        value: c.value,
        label: c.label,
        sublabel: c.path || undefined,
      })),
    [flatCats],
  );

  const handleCreate = useCallback(
    async (name: string) => {
      const newValue = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_|_$/g, "");
      const code = newValue.slice(0, 2).toUpperCase().padEnd(2, "X");

      const newCat: CategoryOption = {
        id: `cat-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        value: newValue,
        label: name,
        code,
        children: [],
      };

      // 1. Optimistically add to local state and select immediately.
      setLocalCategories((prev) => [...prev, newCat]);
      onChange(newValue);

      // 2. Persist to backend in the background.
      try {
        const currentConfig = config?.products || {};
        const currentCats = localCategories;
        await apiClient.put("/api/v1/settings/sections/products", {
          data: {
            ...currentConfig,
            asset_categories: [...currentCats, newCat],
          },
        });
        toast.success("Category added", name);
      } catch (err) {
        // Revert optimistic add on failure.
        setLocalCategories((prev) => prev.filter((c) => c.value !== newValue));
        onChange("");
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data
            ?.detail || "Check your permissions and try again.";
        toast.error("Failed to add category", msg);
      }
    },
    [config, localCategories, onChange],
  );

  return (
    <ComboSelect
      options={options}
      value={value}
      onChange={onChange}
      placeholder="Select category..."
      label={label}
      required={required}
      disabled={disabled}
      creatable={canManage}
      onCreate={handleCreate}
      className={className}
    />
  );
}

export default CategorySelect;
